/**
 * ErrorBoundary —— 兜底渲染异常。
 *
 * React 没把 error boundary 写进 hooks，必须用 class component 配合
 * `getDerivedStateFromError` + `componentDidCatch` 才能拦下子树渲染期抛错。
 *
 * 抓到错以后渲染 ErrorFallback：用 @novel-isr/ui 的 Alert + Button 组合一个
 * 完整页面级降级界面：标题 / 错误描述 / 「重试」按钮（重置 boundary 状态）/
 * 「回首页」按钮。dev 模式下展开 stack 方便排查；prod 不下发 stack。
 *
 * 失败 envelope / fetch 报错走的是 admin-api 的全局 toast，不会触发这个边界；
 * 这里只兜底 React 渲染期 / 同步抛错。
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Alert, Button } from "@novel-isr/ui";
import { FiRefreshCw, FiHome } from "react-icons/fi";
import styles from "./ErrorBoundary.module.scss";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** 自定义降级 UI；不传则用内置 ErrorFallback */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // 真实生产应转发到 sentry / observability；本地开发打到 console 即可
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    const { children, fallback } = this.props;
    if (!error) return children;
    if (fallback) return fallback(error, this.reset);
    return <ErrorFallback error={error} onReset={this.reset} />;
  }
}

interface ErrorFallbackProps {
  error: Error;
  onReset: () => void;
}

function ErrorFallback({ error, onReset }: ErrorFallbackProps) {
  // import.meta.env.DEV 在 Vite 注入；TS 默认 ImportMeta 不带 env，用类型断言简化
  const isDev = (import.meta as { env?: { DEV?: boolean } }).env?.DEV ?? false;
  return (
    <div className={styles.page} role="alert" aria-live="assertive">
      <div className={styles.body}>
        <Alert
          status="danger"
          variant="subtle"
          title="后台界面遇到了一个错误"
        >
          {error.message || "请尝试刷新或返回首页。如果反复出现，把这条错误反馈给开发团队。"}
        </Alert>
        {isDev && error.stack && (
          <details className={styles.stack}>
            <summary>查看堆栈（仅开发模式可见）</summary>
            <pre>{error.stack}</pre>
          </details>
        )}
        <div className={styles.actions}>
          <Button intent="primary" onClick={onReset} leftIcon={<FiRefreshCw />}>
            重试
          </Button>
          <Button
            variant="ghost"
            intent="neutral"
            onClick={() => {
              window.location.assign("/");
            }}
            leftIcon={<FiHome />}
          >
            回首页
          </Button>
        </div>
      </div>
    </div>
  );
}
